import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { VStack, Input, useToast, Textarea, Stack } from "@chakra-ui/react";
import {
    Internship as Entity,
    InternshipApi as EntityApi,
    InternshipSchedule,
    InternshipScheduleApi,
    ServiceApi,
    ServiceGroup,
    ServiceGroupApi,
    Training,
    TrainingApi,
    TypologyApi,
    User,
    UserApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import {
    AppFormControl,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC } from "../../../config";
import { useAuthData } from "../../../contexts";
import { SchedulerDetailView } from "./SchedulerDetailView";

export const InternshipForm: FC<{
    formContainerState: FormContainerStateReturnType;
    isScheduleDetailPage: boolean;
}> = ({ formContainerState, isScheduleDetailPage }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const { user, grant } = useAuthData();

    // state & const
    const USERTYPE = APPC.BE.User.USERTYPE;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<Entity>(new Entity());
    const [loadingService, setLoadingService] = useState(true);
    const [serviceGroupedOptions, setServiceGroupedOptions] = useState<
        { label: string; options: AppSelectOption[] }[]
    >([]);
    const [selectedService, setSelectedService] =
        useState<AppSelectOption | null>(null);
    const [loadingTraining, setLoadingTraining] = useState(true);
    const [trainingData, setTrainingData] = useState<Training[]>([]);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] =
        useState<AppSelectOption | null>(null);
    const [typologyOptions, setTypologyOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTypology, setSelectedTypology] =
        useState<AppSelectOption | null>(null);
    const [loadingInternshipTutor, setLoadingInternshipTutor] = useState(true);
    const [internshipTutorOptions, setInternshipTutorOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedInternshipTutors, setSelectedInternshipTutors] =
        useState<AppSelectOption[]>();

    const [scheduleData, setScheduleData] = useState<InternshipSchedule>(
        new InternshipSchedule()
    );

    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(schema(t)),
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = (formData: Entity) => {
        formData.company = activeSchoolOrCompany?.["@id"];
        formData.capacity = Number(formData.capacity);
        setLoading(true);
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadServiceOptions = () => {
        if (activeSchoolOrCompany?.["@type"] === "Company") {
            const params = {
                [`order[name]`]: "asc",
                pagination: false,
                ["company.id"]: activeSchoolOrCompany?.id,
            };
            if (grant.isInternshipSupervisor) {
                params["services.users.id"] = user?.id;
            }
            setLoadingService(true);
            ServiceGroupApi.getCollectionForSelect<ServiceGroup>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        const options = response.items.map((group) => ({
                            label: group.name,
                            options: (group.services || []).map((service) => ({
                                value: ServiceApi.toResourceIRI(service.id),
                                label: service.name,
                            })),
                        }));
                        setServiceGroupedOptions(options);
                    }
                })
                .finally(() => setLoadingService(false));
        }
    };

    const loadTrainingOptions = () => {
        setLoadingTraining(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };

        TrainingApi.getCollectionForSelect<Training>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTrainingData(response.items);
                    const options = response.items.map((training) => ({
                        value: TrainingApi.toResourceIRI(training.id),
                        label: training.name,
                    }));
                    setTrainingOptions(options);
                }
            })
            .finally(() => setLoadingTraining(false));
    };

    // const loadTypologyOptions = () => {
    //     setLoadingTypology(true);
    //     const params = {
    //         [`order[name]`]: "asc",
    //         pagination: false,
    //     };

    //     TypologyApi.getCollectionForSelect<Typology>(1, params)
    //         .then(({ response }) => {
    //             if (response !== null) {
    //                 const options = response.items.map((typology) => ({
    //                     value: TypologyApi.toResourceIRI(typology.id),
    //                     label: typology.name,
    //                 }));
    //                 setTypologyOptions(options);
    //             }
    //         })
    //         .finally(() => setLoadingTypology(false));
    // };

    const loadUserOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            userType: USERTYPE.USERTYPE_INTERNSHIP_TUTOR,
        };
        if (activeSchoolOrCompany?.["@type"] === "Company") {
            params["companies.id"] = activeSchoolOrCompany?.id;
        }
        setLoadingInternshipTutor(true);
        UserApi.getCollectionForAssign<User>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setInternshipTutorOptions(
                        response?.items?.map((e) => ({
                            value: UserApi.toResourceIRI(e.id),
                            label: e.fullName,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingInternshipTutor(false));
    };

    // useEffect
    useEffect(() => {
        loadServiceOptions();
        loadTrainingOptions();
        loadUserOptions();
    }, []);

    useEffect(() => {
        if (selectedTraining) {
            const training = trainingData.filter(
                (i) => i?.["@id"] === selectedTraining.value
            )[0];
            const options = training?.typologies?.map((opt) => ({
                value: TypologyApi.toResourceIRI(opt.id),
                label: opt.name,
            }));
            setTypologyOptions(options || []);
        } else {
            setTypologyOptions([]);
        }
    }, [selectedTraining]);

    useEffect(() => {
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        if (isScheduleDetailPage) {
            InternshipScheduleApi.getCollectionForSchedulerWithSeat<InternshipSchedule>(
                1,
                { id: formContainerState.entityId }
            )
                .then(async ({ errorMessage, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (response !== null) {
                        setScheduleData(response.items[0]);
                    }
                })
                .finally(() => setLoading(false));
            return;
        }
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    if (
                        response.service &&
                        typeof response.service !== "string"
                    ) {
                        setSelectedService({
                            value: response.service?.["@id"],
                            label: response.service?.name,
                        });
                    }
                    if (
                        response.training &&
                        typeof response.training !== "string"
                    ) {
                        setSelectedTraining({
                            value: response.training?.["@id"],
                            label: response.training?.name,
                        });
                    }
                    if (
                        response.typology &&
                        typeof response.typology !== "string"
                    ) {
                        setSelectedTypology({
                            value: response.typology?.["@id"],
                            label: response.typology?.name,
                        });
                    }
                    if (
                        response.internshipTutors &&
                        typeof response.internshipTutors !== "string"
                    ) {
                        setSelectedInternshipTutors(
                            response?.internshipTutors.map((tutor) => ({
                                value: tutor?.["@id"],
                                label: tutor?.meta?.fullName || "",
                            }))
                        );
                    }

                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    const renderForm = () => {
        return (
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.Internship:name.label")}
                        isInvalid={!!errors.name}
                        message={errors.name?.message}
                        isLoading={loading}
                    >
                        <Input
                            type="text"
                            placeholder={t("ent.Internship:name.placeholder")}
                            {...register("name")}
                            defaultValue={data.name}
                        />
                    </AppFormControl>
                    <AppFormControl
                        label={t("ent.Internship:description.label")}
                        isLoading={loading}
                    >
                        <Textarea
                            noOfLines={6}
                            minH={"130px"}
                            {...register("description")}
                            placeholder={t(
                                "ent.Internship:description.placeholder"
                            )}
                            defaultValue={data.description}
                        />
                    </AppFormControl>
                    <Stack
                        gap={5}
                        direction={{ base: "column", sm: "column", md: "row" }}
                    >
                        <AppFormControl
                            label={t("ent.Internship:service.label")}
                            isInvalid={!!errors.service}
                            message={errors.service?.message}
                            isLoading={loading}
                        >
                            <Controller
                                control={control}
                                name="service"
                                defaultValue={selectedService?.value as string}
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        isLoading={loadingService}
                                        options={serviceGroupedOptions}
                                        value={selectedService || null}
                                        placeholder={t(
                                            "ent.Internship:service.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedService(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("con.User:USERTYPE_INTERNSHIP_TUTOR")}
                            isLoading={loading}
                        >
                            <Controller
                                control={control}
                                name="internshipTutors"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption, true>
                                        name={field.name}
                                        isClearable
                                        isMulti={true}
                                        isLoading={loadingInternshipTutor}
                                        options={internshipTutorOptions}
                                        value={selectedInternshipTutors || null}
                                        placeholder={t(
                                            "ent.Internship:internshipTutor.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(
                                                e?.map(
                                                    (option) => option?.value
                                                ) || []
                                            );
                                            setSelectedInternshipTutors(
                                                e as AppSelectOption[]
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                    </Stack>
                    <Stack
                        gap={5}
                        direction={{ base: "column", sm: "column", md: "row" }}
                    >
                        <AppFormControl
                            label={t("ent.Internship:training.label")}
                            isInvalid={!!errors.training}
                            message={errors.training?.message}
                            isLoading={loading}
                        >
                            <Controller
                                control={control}
                                name="training"
                                defaultValue={selectedTraining?.value as string}
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        isLoading={loadingTraining}
                                        options={trainingOptions}
                                        value={selectedTraining || null}
                                        placeholder={t(
                                            "ent.Internship:training.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedTraining(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("ent.Internship:typology.label")}
                            isInvalid={!!errors.typology}
                            message={errors.typology?.message}
                            isLoading={loading}
                        >
                            <Controller
                                control={control}
                                name="typology"
                                defaultValue={selectedTypology?.value as string}
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        isLoading={loadingTraining}
                                        options={typologyOptions}
                                        value={selectedTypology || null}
                                        placeholder={t(
                                            "ent.Internship:typology.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedTypology(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t("ent.Internship:capacity.label")}
                            isLoading={loading}
                        >
                            <Input
                                type="number"
                                placeholder={t(
                                    "ent.Internship:capacity.placeholder"
                                )}
                                {...register("capacity")}
                                defaultValue={data.capacity}
                            />
                        </AppFormControl>
                    </Stack>
                    <Stack
                        gap={5}
                        direction={{ base: "column", sm: "column", md: "row" }}
                    ></Stack>
                    {/* <AppFormControl
                        label={t("ent.Internship:years.label")}
                        isInvalid={!!errors.years}
                        message={errors.years?.message}
                        isLoading={loading}
                    >
                        <Controller
                            control={control}
                            name="years"
                            defaultValue={data?.years || []}
                            render={({ field }) => (
                                <CheckboxGroup
                                    value={field.value ?? []}
                                    onChange={field.onChange}
                                >
                                    <HStack alignItems="start" w="full">
                                        <Checkbox value="FIRST">
                                            {t("con.Internship:YEARS_FIRST")}
                                        </Checkbox>
                                        <Checkbox value="SECOND">
                                            {t("con.Internship:YEARS_SECOND")}
                                        </Checkbox>
                                        <Checkbox value="THIRD">
                                            {t("con.Internship:YEARS_THIRD")}
                                        </Checkbox>
                                    </HStack>
                                </CheckboxGroup>
                            )}
                        />
                    </AppFormControl> */}
                </VStack>
            </form>
        );
    };

    const renderDetail = () => {
        return <SchedulerDetailView data={scheduleData} />;
    };

    return (
        <AppFormContainer
            title={
                isScheduleDetailPage
                    ? t("padm.InternshipSchedulePage:text.pageTitle")
                    : t("padm.InternshipForm:text.pageTitle")
            }
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
            size={"xl"}
            isFooter={!isScheduleDetailPage}
        >
            {isScheduleDetailPage ? renderDetail() : renderForm()}
        </AppFormContainer>
    );
};
